// Utility for Floor Color parsing, tonal shade calculations and isometric checkerboard styling

export const TAILWIND_COLOR_MAP: Record<string, string> = {
  // Cyan
  'bg-cyan-500': '#06b6d4',
  'bg-cyan-600': '#0891b2',
  'bg-cyan-700': '#0e7490',
  // Emerald / Green
  'bg-emerald-500': '#10b981',
  'bg-emerald-600': '#059669',
  'bg-emerald-700': '#047857',
  'bg-lime-600': '#65a30d',
  'bg-green-600': '#16a34a',
  // Amber / Orange / Wood
  'bg-amber-500': '#f59e0b',
  'bg-amber-600': '#d97706',
  'bg-amber-700': '#b45309',
  'bg-amber-800': '#92400e',
  'bg-orange-600': '#ea580c',
  'bg-orange-700': '#c2410c',
  // Blue / Sky
  'bg-sky-500': '#0ea5e9',
  'bg-sky-600': '#0284c7',
  'bg-blue-600': '#2563eb',
  'bg-indigo-600': '#4f46e5',
  'bg-indigo-900': '#312e81',
  // Purple / Violet
  'bg-purple-500': '#a855f7',
  'bg-purple-600': '#9333ea',
  'bg-violet-600': '#7c3aed',
  'bg-fuchsia-600': '#c026d3',
  // Rose / Red
  'bg-rose-500': '#f43f5e',
  'bg-rose-600': '#e11d48',
  'bg-red-600': '#dc2626',
  // Slate / Gray / Dark / Light
  'bg-slate-200': '#e2e8f0',
  'bg-slate-400': '#94a3b8',
  'bg-slate-600': '#475569',
  'bg-slate-700': '#334155',
  'bg-slate-800': '#1e293b',
  'bg-slate-900': '#0f172a',
  'bg-zinc-800': '#27272a',
  'bg-yellow-500': '#eab308',
  'bg-teal-600': '#0d9488',
};

/**
 * Resolves a Tailwind class string, hex color, or rgb string into a clean #RRGGBB hex code.
 */
export function resolveColorToHex(colorStr?: string, fallback = '#0891b2'): string {
  if (!colorStr) return fallback;

  // Trim string
  const clean = colorStr.trim();

  // If it's a known Tailwind background class
  if (TAILWIND_COLOR_MAP[clean]) {
    return TAILWIND_COLOR_MAP[clean];
  }

  // If it's a partial class like "cyan-600"
  if (TAILWIND_COLOR_MAP[`bg-${clean}`]) {
    return TAILWIND_COLOR_MAP[`bg-${clean}`];
  }

  // If it starts with #
  if (clean.startsWith('#')) {
    // 3-char hex to 6-char
    if (clean.length === 4) {
      return `#${clean[1]}${clean[1]}${clean[2]}${clean[2]}${clean[3]}${clean[3]}`;
    }
    if (clean.length >= 7) {
      return clean.slice(0, 7);
    }
  }

  return fallback;
}

/**
 * Adjusts the brightness of a hex color by a given percentage.
 * @param hex - Color in #RRGGBB format
 * @param percent - E.g. -20 for 20% darker, +25 for 25% lighter
 */
export function adjustColorBrightness(hex: string, percent: number): string {
  const cleanHex = resolveColorToHex(hex);
  let num = parseInt(cleanHex.replace('#', ''), 16);
  if (isNaN(num)) num = 0x0891b2;

  let r = (num >> 16) + Math.round((255 * percent) / 100);
  let g = ((num >> 8) & 0x00ff) + Math.round((255 * percent) / 100);
  let b = (num & 0x0000ff) + Math.round((255 * percent) / 100);

  r = Math.min(255, Math.max(0, r));
  g = Math.min(255, Math.max(0, g));
  b = Math.min(255, Math.max(0, b));

  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

/**
 * Calculates primary (even) and secondary (odd) tones for checkerboard rendering.
 */
export function getCheckerboardColorTones(
  primaryColor: string,
  customSecondaryColor?: string,
  hasCheckerboard = true,
  brightnessDelta = -18
): { primary: string; secondary: string; highlight: string; shadow: string } {
  const primary = resolveColorToHex(primaryColor);
  
  if (!hasCheckerboard) {
    return {
      primary,
      secondary: primary,
      highlight: adjustColorBrightness(primary, +20),
      shadow: adjustColorBrightness(primary, -20),
    };
  }

  const secondary = customSecondaryColor
    ? resolveColorToHex(customSecondaryColor)
    : adjustColorBrightness(primary, brightnessDelta);

  return {
    primary,
    secondary,
    highlight: adjustColorBrightness(primary, +25),
    shadow: adjustColorBrightness(secondary, -20),
  };
}
